import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useSystemConfig } from '@/lib/queries';
import { Icon, type IconName } from '@/components/icons/Icon';
import { sectorPath, useSector } from '@/lib/sector';
import { Logo } from './BuyerLayout';

/**
 * The site footer, rendered entirely from admin-controlled settings.
 *
 * Every value here — the logo, the tagline, the address, the support number,
 * the WhatsApp line, the social links, the legal pages — comes from
 * `/system/config`, which an admin edits in the console. Changing the support
 * number is a settings save, not a deploy, and the React Native app reads the
 * same payload.
 *
 * ── THE SHAPE: ONE WIDE COLUMN, THEN THREE NARROW ONES ──────────────────────
 *
 * The left column is not a link list. It is the brand, a sentence about what
 * ChiCare is, a search box and the social row — the things somebody looks at
 * rather than scans. It is roughly half again as wide as the others because a
 * sentence and a search field both need a measure, and squeezing them into a
 * quarter of the footer is what made the old one look ragged.
 *
 * The three link columns to its right are equal width and their rows line up,
 * which is the whole difference between a footer that reads as a grid and one
 * that reads as a zigzag. Nothing in them is a sentence; every item is two or
 * three words, so no column can wrap out of step with its neighbours.
 *
 * ── WHY THE SEARCH BOX IS DOWN HERE AT ALL ──────────────────────────────────
 *
 * Because the bottom of a long page is exactly where somebody has finished
 * reading and still has not found what they came for. Scrolling back to the
 * header to search is a tax on the one person the footer could still help. It
 * submits into the sector being browsed, like every other search on the site.
 *
 * ── THE MOBILE LAYOUT IS A DIFFERENT LAYOUT, NOT A SQUEEZED ONE ─────────────
 *
 * The old footer held three columns at 320px, where each one is about 88px
 * wide. It fitted, and it was wrong: 88px is not a column, and the headings
 * stayed level only because every item was shortened until it did.
 *
 * So on a phone the widget block is full width and the link columns go to TWO,
 * at a readable size, with the rows still aligned. Two columns of 140px read as
 * a footer; three of 88px read as a crease. Three columns return at 640px,
 * where they are 150px each, and the fourth joins at 1024px.
 *
 * ── AND THE ROWS ARE TIGHTER THAN THEY WERE ─────────────────────────────────
 *
 * They were 44px tall each, which is the §11 touch target — and stacked eight
 * deep it made the footer taller than some of the pages above it. The rows are
 * now 32px of line with the target made up by padding, so the ink is tight and
 * the tappable box is not. On a coarse pointer — a real phone, not a narrow
 * desktop window — they go back to 40px, because a thumb is not a mouse.
 */
/**
 * The social networks, by their setting key.
 *
 * ── WHY A TABLE AND NOT `capitalize` ───────────────────────────────────────
 *
 * The keys are setting names: `x`, `tiktok`, `linkedin`. CSS `capitalize` made
 * those "X", "Tiktok" and "Linkedin" — two of which are not how the companies
 * write their own names, on the page where ChiCare asks to be followed.
 *
 * ── AND WHY THE ICON LIVES HERE TOO ────────────────────────────────────────
 *
 * One table, so a network cannot end up with a name and no mark or a mark and
 * no name. These five are exactly what Settings offers (SettingsRegistry:
 * social.*); anything else an API ever sends still renders, with its key as
 * the label and the generic link glyph.
 */
const SOCIALS: Record<string, { label: string; icon: IconName }> = {
  instagram: { label: 'Instagram', icon: 'brand-instagram' },
  facebook: { label: 'Facebook', icon: 'brand-facebook' },
  x: { label: 'X', icon: 'brand-x' },
  linkedin: { label: 'LinkedIn', icon: 'brand-linkedin' },
  tiktok: { label: 'TikTok', icon: 'brand-tiktok' },
};

export function SiteFooter() {
  const { data: config } = useSystemConfig();
  const sector = useSector();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');

  if (!config) return null;

  const brand = (config as ConfigWithCms).brand;
  const company = (config as ConfigWithCms).company;
  const contact = (config as ConfigWithCms).contact;
  const social = (config as ConfigWithCms).social ?? {};
  const pages = (config as ConfigWithCms).legal_pages ?? [];

  // Whichever page the seller pitch points at, so the filter below follows an
  // admin who moves it rather than hard-coding one slug.
  const sellerPitchSlug = (
    config as { seller_pitch?: { page_slug?: string } } | undefined
  )?.seller_pitch?.page_slug?.trim();

  const path = (to: string) => sectorPath(sector, to);

  const whatsapp = contact?.whatsapp
    ? `https://wa.me/${contact.whatsapp.replace(/\D/g, '').replace(/^0/, '234')}`
    : null;

  const address = [company?.address_line1, company?.address_line2]
    .concat([[company?.city, company?.state, company?.country].filter(Boolean).join(', ')])
    .filter(Boolean) as string[];

  /*
   * ── 876px TALL ON A PHONE, WHICH IS TALLER THAN THE PHONE ─────────────────
   *
   * Measured at 393×852: the footer was 876px. A buyer who reached the bottom
   * of the home shelf had a full extra screen of links to scroll past, every
   * one of them leading away from what they were doing.
   *
   * What went: the search box (the app bar and the Search tab both already
   * carry one), a duplicate "Sell on ChiCare", and a lot of vertical padding.
   * Nothing reachable ONLY from here was removed.
   */
  return (
    <footer className="mt-6 border-t border-line bg-white sm:mt-10">
      <div className="mx-auto w-full max-w-[1200px] px-4 pb-3 pt-4 sm:pb-6 sm:pt-9 xl:px-6">
        {/* The track counts ARE the layout:
              • under 640px  2 columns, with the widget across both
              • 640–1023px   3 columns, with the widget across all three
              • 1024px up    the widget takes its own wide track on the left

            So the three link columns are 2-up on a phone and 3-up everywhere
            else, and on every row the headings are level. Three 88px columns
            on a phone is what the old footer did, and 88px is not a column,
            it is a crease. */}
        <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-3 sm:gap-x-8 sm:gap-y-8 lg:grid-cols-[minmax(0,1.45fr)_repeat(3,minmax(0,1fr))] lg:gap-x-10">
          {/* ── The widget column ──────────────────────────────────────── */}
          <div className="col-span-2 min-w-0 sm:col-span-3 lg:col-span-1">
            <Link
              to={path('/')}
              className="inline-flex items-center gap-1.5 font-display text-[19px] font-extrabold tracking-[-0.02em] text-blue no-underline"
            >
              {brand?.logo_id?.url ? (
                <img
                  src={brand.logo_id.url}
                  alt={brand.logo_id.alt ?? brand.name ?? 'ChiCare'}
                  className="h-7 w-auto"
                />
              ) : (
                <>
                  <span className="grid h-[27px] w-[27px] place-items-center rounded-[7px] bg-blue">
                    <Logo className="h-4 w-4 fill-white" />
                  </span>
                  {brand?.name ?? 'ChiCare'}
                </>
              )}
            </Link>

            {brand?.tagline ? (
              <p className="m-0 mt-1.5 max-w-[42ch] text-[12px] leading-snug text-muted sm:mt-2.5 sm:text-[12.5px] sm:leading-[1.6]">
                {brand.tagline}
              </p>
            ) : null}

            {/* ── Search ─────────────────────────────────────────────────
                A plain form, so Enter works and so it still works with
                JavaScript halfway through loading. Not the shared
                `SearchField`: that one is a 44px header control with a clear
                button, and at this size and weight it would be the loudest
                thing in the footer. */}
            <form
              role="search"
              onSubmit={(event) => {
                event.preventDefault();

                const query = term.trim();

                if (!query) return;

                navigate(`${path('/search')}?q=${encodeURIComponent(query)}`);
                setTerm('');
              }}
              /* ── NOT ON A PHONE ──────────────────────────────────
                 The app bar carries a search field at the top of every screen
                 and the tab bar carries a Search tab at the bottom of every
                 screen. A third one at the end of the page is ~70px of height
                 to offer somebody a thing they have already scrolled past.
                 It earns its place on a desktop, where the footer is a
                 sitemap and there is no tab bar. */
              className="mt-4 hidden max-w-[340px] items-center gap-1.5 rounded-[10px] border border-line bg-paper px-2.5 py-1.5 focus-within:border-blue focus-within:bg-white focus-within:ring-2 focus-within:ring-blue/15 sm:flex"
            >
              <Icon name="search" size={15} className="shrink-0 text-faint" />
              <label htmlFor="footer-search" className="sr-only">
                Search ChiCare
              </label>
              <input
                id="footer-search"
                type="search"
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                placeholder="Search medicines"
                data-testid="footer-search"
                className="min-w-0 flex-1 border-0 bg-transparent py-1 text-[12.5px] text-ink outline-none placeholder:text-faint"
              />
              {/* A real target, not a 28px square. The ROW stays the height it
                  was — `-my-2` pulls the extra back out of the layout — so this
                  costs nothing visually and gives a thumb something to hit.
                  Everything else in this footer is a link somebody reads past;
                  this one DOES something, and a mis-tap on it is a search that
                  never ran. */}
              <button
                type="submit"
                aria-label="Search"
                className="-my-2 grid h-11 w-9 shrink-0 place-items-center rounded-[7px] text-blue"
              >
                <Icon name="chevron" size={16} strokeWidth={2.2} />
              </button>
            </form>

            {Object.values(social).some(Boolean) ? (
              <div className="mt-2 flex flex-wrap gap-1.5 sm:mt-4">
                {Object.entries(social).map(([network, url]) => {
                  if (!url) {
                    return null;
                  }

                  const brand = SOCIALS[network];

                  return (
                    <a
                      key={network}
                      href={url}
                      rel="noreferrer noopener"
                      target="_blank"
                      aria-label={`ChiCare on ${brand?.label ?? network}`}
                      className="flex h-8 items-center gap-1.5 rounded-lg border border-line px-2 text-[10.5px] font-medium text-muted no-underline hover:border-blue hover:text-blue sm:h-9 sm:px-2.5 sm:text-[11px]"
                    >
                      {/* ── THE MARK, AND WHY IT IS FILLED ──────────────
                          Brand marks are the one exception in the icon set:
                          at 13px an outlined logo is a few grey strokes that
                          could be anything, and recognising it instantly is
                          the whole reason it is here.

                          A network with no mark falls back to the generic
                          link glyph, OUTLINED — rendering an outline path
                          with `filled` gives a solid blob. */}
                      <Icon
                        name={brand?.icon ?? 'link'}
                        filled={Boolean(brand)}
                        size={13}
                        className="shrink-0"
                      />
                      {/* `whitespace-nowrap`, because "LinkedIn" breaking
                          after "Linked" is worse than one more row. */}
                      <span className="whitespace-nowrap capitalize">
                        {brand?.label ?? network}
                      </span>
                    </a>
                  );
                })}
              </div>
            ) : null}
          </div>

          {/* ── Get help ───────────────────────────────────────────────── */}
          <Column heading="Get help" label="Support">
            {/* Support first, above the phone number. It is the one of
                these that answers in minutes rather than when somebody picks
                up, and it is where this column's other entries end up anyway
                if nobody does. */}
            <Row to={path('/support')} icon="message" label="Support" value="Chat with us" />

            {contact?.support_phone ? (
              <Row
                href={`tel:${contact.support_phone.replace(/\s/g, '')}`}
                icon="phone"
                label="Call us"
                value={contact.support_phone}
              />
            ) : null}

            {whatsapp ? (
              <Row
                href={whatsapp}
                icon="message"
                label="WhatsApp"
                value={contact?.whatsapp}
                external
              />
            ) : null}

            {contact?.support_email ? (
              <Row
                href={`mailto:${contact.support_email}`}
                icon="doc"
                label="Email us"
                value={contact.support_email}
              />
            ) : null}

            {contact?.hours ? (
              // A fact, not a link, so it is not in the list. One line, because
              // it is the hours and not an explanation of them.
              <li className="flex items-start gap-1.5 pt-1 text-[11px] leading-snug text-faint">
                <Icon name="clock" size={12} className="mt-[2px] shrink-0" />
                {/* text-balance so a wrap lands between the DAYS and the
                    HOURS rather than inside "8am – 8pm", which splits a range
                    down the middle and reads as two different facts. */}
                <span className="text-balance">{contact.hours}</span>
              </li>
            ) : null}
          </Column>

          {/* ── Browse ─────────────────────────────────────────────────── */}
          <Column heading="Browse" label="Shopping">
            <Row to={path('/search')} label="All medicines" />
            <Row to={path('/blog')} label="Blog" />
            <Row to="/verify" label="Verify a pack" />
            {/* "Prescriptions", not "Send a prescription": at 320px a link
                column is about 128px and the longer label truncates. A
                truncated navigation label is a worse answer than a shorter one
                that fits. */}
            <Row to={path('/prescriptions')} label="Prescriptions" />
            <Row
              to={sector === 'veterinary' ? '/' : '/vet'}
              label={sector === 'veterinary' ? 'Pharmacy' : 'Animal health'}
            />
          </Column>

          {/* ── ChiCare ────────────────────────────────────────────────── */}
          {/* Across BOTH phone columns. Squeezed into one, the wrapped legal
              links ran to three ragged lines; across the full width they are
              two and read as a line of links rather than a stack. */}
          <Column
            heading="ChiCare"
            label="About and legal"
            className="col-span-2 sm:col-span-1"
          >
            {/* ── ONE "Sell on ChiCare", NOT TWO ─────────────────────
                This row points at the registration form. The CMS page titled
                "Sell on ChiCare" is the long write-up BEHIND that form, and it
                was being listed here as well — two rows, the same five words,
                two different destinations, one directly under the other.

                The write-up is still reachable: from the form itself, and from
                the account screen's "Read full details". */}
            {/* ── ONE WRAPPED LINE ON A PHONE, A COLUMN ON A DESKTOP ─────
                Six labels of two or three words each were six 40px rows —
                roughly 240px to say Sell, Help, Privacy, Returns, Terms.
                Wrapped they are two lines.

                Nothing is lost by it: these are read once, if ever, unlike the
                support links above them which somebody comes back to. The tap
                height stays comfortable through the padding, so the ROW got
                shorter and the TARGET did not. */}
            <li className="-my-1 flex flex-wrap items-center gap-x-3 sm:hidden">
              {[
                { to: '/sell', title: 'Sell on ChiCare' },
                ...pages
                  .filter((page) => page.slug !== sellerPitchSlug)
                  .map((page) => ({ to: `/pages/${page.slug}`, title: page.title })),
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="inline-flex min-h-9 items-center whitespace-nowrap text-[12px] leading-snug text-muted no-underline hover:text-blue"
                >
                  {item.title}
                </Link>
              ))}
            </li>

            <div className="hidden sm:contents">
              <Row to="/sell" label="Sell on ChiCare" />
              {pages
                .filter((page) => page.slug !== sellerPitchSlug)
                .map((page) => (
                  <Row key={page.slug} to={`/pages/${page.slug}`} label={page.title} />
                ))}
            </div>
          </Column>
        </div>

        {/* ── The safety line ──────────────────────────────────────────────
            §4.6 routes counterfeit and adverse-reaction reports to a monitored
            address that is never auto-replied to. It sits apart from ordinary
            support, in the danger colour, across the full width, because
            somebody holding a pack they do not trust should not have to read
            four other links first — and because a sentence in a quarter-width
            column wraps to three ragged lines and drags that column out of
            step with its neighbours. */}
        {contact?.safety_email ? (
          <div className="mt-4 border-t border-line-2 pt-3 sm:mt-8 sm:pt-4">
            <a
              href={`mailto:${contact.safety_email}`}
              data-testid="footer-safety"
              title={contact.safety_email}
              // 44px, not 40. §4.6 makes this the one control on the page
              // that must never be hard to reach — it is pressed by somebody
              // holding a pack they do not trust — so it does not take the
              // footer's tighter row height.
              className="-mx-1 inline-flex min-h-11 items-center gap-1.5 rounded-btn px-1 text-[12.5px] font-semibold text-danger no-underline hover:underline"
            >
              <Icon name="alert" size={14} className="shrink-0" />
              Suspect a fake pack? Report it — you are refunded first.
            </a>
          </div>
        ) : null}
      </div>

      {/* ── The bottom bar ───────────────────────────────────────────────
          The licence promise and the copyright. Wraps rather than squeezing,
          because the promise is the part worth reading and a copyright line has
          never needed to share a row with anything.

          The registered NAME lives here and nowhere else: "ChiCare
          Technologies Limited" wraps to three ragged lines in a footer column,
          and it is printed in full on this line anyway. */}
      <div className="border-t border-line-2 bg-paper px-4 py-2.5 sm:py-3.5 xl:px-6">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-faint">
          {/* No mark in front of it. The sentence is the promise, and a small
              green shield beside 11px grey text did not reinforce it — it just
              put the same shape here that was haunting the trust banner. The
              words carry themselves. */}
          <p className="m-0">Every pharmacy on ChiCare holds a current PCN premises licence.</p>

          {/* Monospace on the RC number, like every other reference in the
              product: it is compared character by character against a document,
              and a 1 that looks like an l fails that silently. */}
          {company?.rc_number ? (
            <span className="font-mono text-[10.5px]">{company.rc_number}</span>
          ) : null}

          {address.length > 0 ? (
            <address className="m-0 hidden not-italic lg:inline">{address.join(', ')}</address>
          ) : null}

          <span className="sm:ml-auto">
            © {new Date().getFullYear()} {company?.legal_name ?? 'ChiCare'}
          </span>
        </div>
      </div>
    </footer>
  );
}

/**
 * One link column.
 *
 * `label` is for assistive tech and `heading` is what is printed. They differ
 * where the printed word is too terse to stand alone out of context —
 * "ChiCare" means something under a visible column of legal links and nothing
 * at all read aloud on its own.
 */
function Column({
  heading,
  label,
  children,
  className,
}: {
  heading: string;
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <nav aria-label={label} className={`min-w-0 ${className ?? ''}`}>
      <h2 className="m-0 text-[10.5px] font-bold uppercase tracking-[0.07em] text-faint">
        {heading}
      </h2>
      <ul className="m-0 mt-2 list-none space-y-px p-0">{children}</ul>
    </nav>
  );
}

/**
 * One row in a column.
 *
 * `min-h-8` with `py-1` is 32px of row and a 32px box. §11 asks for 44px, and
 * that applies where a mis-tap costs something — a quantity stepper, a
 * checkout button, a report link. Eight 44px rows in a footer is 352px of
 * mostly empty white, which is the "line height is too much" this replaces.
 *
 * The compromise is honest rather than clever: `pointer-coarse` is a real
 * touchscreen, and there the rows go back to 40px. A narrow desktop window is
 * not a phone, and sizing for a thumb that is not there costs the layout.
 */
function Row({
  to,
  href,
  icon,
  label,
  value,
  external,
}: {
  to?: string;
  href?: string;
  icon?: 'phone' | 'message' | 'doc';
  label: string;
  value?: string;
  external?: boolean;
}) {
  const className =
    '-mx-1 flex min-h-8 items-center gap-1.5 rounded-btn px-1 py-0.5 text-[12.5px] leading-snug text-muted no-underline hover:text-blue pointer-coarse:min-h-9 sm:py-1 sm:pointer-coarse:min-h-10';

  const inner = (
    <>
      {icon ? <Icon name={icon} size={13} className="shrink-0" /> : null}
      <span className="truncate">{label}</span>
    </>
  );

  return (
    <li>
      {href ? (
        <a
          href={href}
          // The value is in the `title` rather than printed. A support number
          // under every link is four more rows of footer, and nobody reads a
          // number off a screen to dial it — they tap it.
          title={value}
          {...(external ? { rel: 'noreferrer noopener', target: '_blank' } : {})}
          className={className}
        >
          {inner}
        </a>
      ) : (
        <Link to={to ?? '/'} className={className}>
          {inner}
        </Link>
      )}
    </li>
  );
}

/**
 * `/system/config` carries the whole admin-owned CMS payload alongside the
 * business rules. Typed here rather than in the shared package because the
 * exact set of keys is whatever `SettingsRegistry` marks public, and pinning
 * that too tightly would make adding a setting a two-repo change.
 */
interface ConfigWithCms {
  brand?: {
    name?: string;
    tagline?: string;
    logo_id?: { url: string; alt: string | null } | null;
  };
  company?: Record<string, string | undefined>;
  contact?: Record<string, string | undefined>;
  social?: Record<string, string | undefined>;
  legal_pages?: Array<{ slug: string; title: string; placement: string | null }>;
}
